<template>
  <main id="main-content" ref="root" class="landing landing--light" tabindex="-1">
    <LandingPageBackground />
    <div class="landing__content">
      <a href="#main-content" class="skip-link">К основному содержанию</a>

      <LandingHeroSection @open-access="openAccessModal" />
      <LandingDemoDashboard class="reveal" @open-quiz="openAccessModal" />
      <LandingFeaturesSection class="reveal" />

      <section id="faq" class="faq landing-section reveal">
        <div class="landing-container">
          <header class="section-header">
            <p class="eyebrow">Частые вопросы</p>
            <h2 class="section-title">Ответы о системе</h2>
            <p class="section-lead">
              Частые вопросы о возможностях платформы и подключении.
            </p>
          </header>
          <div class="faq__grid">
            <ul v-for="(col, colIdx) in faqColumns" :key="colIdx" class="faq__list">
              <li v-for="(item, idx) in col" :key="`${colIdx}-${idx}`">
                <details class="faq__item">
                  <summary class="faq__q">{{ item.q }}</summary>
                  <p class="faq__a">{{ item.a }}</p>
                </details>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <footer class="foot landing-section">
        <div class="landing-container foot__inner">
          <span>© {{ year }} AI-Tenders</span>
          <a href="mailto:Ai@neyrosvet.ru">Ai@neyrosvet.ru</a>
          <a href="tel:+79533416548">+7 (953) 341-65-48</a>
          <router-link to="/offer">Оферта</router-link>
          <router-link to="/privacy">Политика персональных данных</router-link>
          <span>{{ operatorRequisitesLine }}</span>
        </div>
      </footer>

      <LandingAccessModal :show="accessModalOpen" @close="closeAccessModal" />
      <SalesContactFab @open-quiz="openAccessModal" />
    </div>
  </main>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import LandingAccessModal from '@/components/landing/LandingAccessModal.vue'
import LandingDemoDashboard from '@/components/landing/LandingDemoDashboard.vue'
import LandingFeaturesSection from '@/components/landing/LandingFeaturesSection.vue'
import LandingHeroSection from '@/components/landing/LandingHeroSection.vue'
import LandingPageBackground from '@/components/landing/LandingPageBackground.vue'
import { operatorRequisitesLine } from '@/content/legal/operator'
import { useLandingSeo } from '@/composables/useLandingSeo'
import { LANDING_FAQ } from '@/landing/faq'
import '@/styles/landing-access-cta.css'
import { markLeadSessionStart } from '@/utils/crmLead'
import SalesContactFab from '@/components/landing/SalesContactFab.vue'

useLandingSeo()

const route = useRoute()
const root = ref(null)
const accessModalOpen = ref(false)
const year = new Date().getFullYear()
const faqItems = LANDING_FAQ

const faqColumns = computed(() => {
  const mid = Math.ceil(faqItems.length / 2)
  return [faqItems.slice(0, mid), faqItems.slice(mid)]
})

let obs = null

function openAccessModal() {
  accessModalOpen.value = true
}

function closeAccessModal() {
  accessModalOpen.value = false
}

function initReveal() {
  const el = root.value
  if (!el) return
  const nodes = el.querySelectorAll('.reveal')
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    nodes.forEach((n) => n.classList.add('on'))
    return
  }
  obs = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('on')
          obs.unobserve(entry.target)
        }
      })
    },
    { threshold: 0.12 },
  )
  nodes.forEach((n) => obs.observe(n))
}

onMounted(() => {
  markLeadSessionStart()
  initReveal()
  if (route.query.funnel === 'open') {
    accessModalOpen.value = true
  }
})

watch(
  () => route.query.funnel,
  (v) => {
    if (v === 'open') accessModalOpen.value = true
  },
)

onUnmounted(() => {
  obs?.disconnect()
})
</script>

<style scoped>
.landing.landing--light {
  position: relative;
  --fg: #0f172a;
  --muted: #516078;
  --muted-strong: #3f4f67;
  --blue: #1e3a8a;
  --blue-accent: #1d4ed8;
  --surface: #ffffff;
  --surface-alt: #f4f7fb;
  --surface-soft: #ecf1f8;
  --border: #d2dbe8;
  --border-strong: #b8c6d9;
  --text-display: clamp(2rem, 5vw, 2.75rem);
  --text-h2: clamp(1.35rem, 3vw, 1.85rem);
  --text-h3: 1.05rem;
  --text-lead: 1.02rem;
  --text-body: 1rem;
  --text-sm: 0.875rem;
  --text-xs: 0.75rem;
  --container-max: 1200px;
  --nav-h: 0rem;
  --section-space: clamp(3.1rem, 7vw, 4.8rem);
  --radius-soft: 12px;
  background: transparent;
  color: var(--fg);
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: var(--text-body);
}

.landing__content {
  position: relative;
  z-index: 1;
}

.landing :is(a, button):focus-visible {
  outline: 2px solid var(--blue-accent);
  outline-offset: 3px;
}

.skip-link {
  position: absolute;
  left: -9999px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.skip-link:focus {
  position: fixed;
  left: 1rem;
  top: 1rem;
  z-index: 200;
  width: auto;
  height: auto;
  padding: 0.65rem 1rem;
  background: var(--blue);
  color: #fff;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  overflow: visible;
}

.landing :deep(.landing-container) {
  width: 100%;
  max-width: var(--container-max);
  margin-left: auto;
  margin-right: auto;
  padding-left: clamp(1rem, 4vw, 2rem);
  padding-right: clamp(1rem, 4vw, 2rem);
  box-sizing: border-box;
}

.landing-section {
  scroll-margin-top: 1rem;
}

.section-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 0 auto 2.35rem;
  text-align: center;
  gap: 0.1rem;
}

.section-header .section-lead {
  width: 100%;
  max-width: 44rem;
  margin-bottom: 0;
  color: var(--muted);
  line-height: 1.6;
}

.section-title {
  font-size: var(--text-h2);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin: 0 0 0.75rem;
  text-align: center;
  color: var(--fg);
}

.eyebrow {
  font-size: var(--text-xs);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 600;
  margin: 0 0 0.55rem;
  color: var(--muted);
  text-align: center;
}

.faq {
  padding: var(--section-space) 0;
}

.faq__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.25rem;
  align-items: start;
}

.faq__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.faq__item {
  border-radius: var(--radius-soft);
  border: 1px solid var(--border-strong);
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  overflow: hidden;
}

.faq__q {
  padding: 1rem 1.1rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--fg);
  cursor: pointer;
  list-style: none;
  line-height: 1.4;
}

.faq__q::-webkit-details-marker {
  display: none;
}

.faq__item[open] .faq__q {
  color: var(--blue-accent);
}

.faq__a {
  margin: 0;
  padding: 0 1.1rem 1rem;
  font-size: var(--text-sm);
  line-height: 1.55;
  color: var(--muted);
}

.foot {
  padding: 1.8rem 0;
  border-top: 1px solid var(--border-strong);
}

.foot__inner {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.85rem 1.5rem;
  font-size: var(--text-sm);
  color: var(--muted);
}

.foot a {
  color: #475569;
  text-decoration: none;
}

.reveal {
  opacity: 0;
  transform: translateY(18px);
  transition: 0.75s cubic-bezier(0.22, 1, 0.36, 1);
}

.reveal.on {
  opacity: 1;
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  .reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

@media (max-width: 900px) {
  .faq__grid {
    grid-template-columns: 1fr;
  }
}
</style>
